import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, KeyRound, Copy } from "lucide-react";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll");
      setEnroll(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start enrollment"); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (!code) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-Factor Authentication enabled");
      setEnroll(null); setCode("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    if (!code || !password) return toast.error("Password + 6-digit code required");
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code, password });
      toast.success("Two-Factor Authentication disabled");
      setCode(""); setPassword("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to disable"); }
    finally { setLoading(false); }
  };

  const enabled = status?.enabled;
  const required = status?.required;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <div className="label-mono mb-2">Security</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Two-Factor Authentication</h1>
        <p className="mt-2 text-sm text-stone-600">Protect your account with a time-based one-time password (TOTP) from Google Authenticator, 1Password, Authy or similar.</p>
      </div>

      <div className={`card-flat border-l-4 ${enabled ? "border-l-emerald-500" : required ? "border-l-amber-500" : "border-l-stone-300"}`}>
        <div className="flex items-start gap-4">
          {enabled ? <ShieldCheck className="h-8 w-8 text-emerald-600" strokeWidth={1.5} />
                   : <ShieldAlert className="h-8 w-8 text-amber-600" strokeWidth={1.5} />}
          <div className="flex-1">
            <div className="font-display text-xl font-medium text-slate-950">
              {enabled ? "2FA is enabled" : "2FA is not enabled"}
            </div>
            <p className="mt-1 text-sm text-stone-600">
              {enabled ? "Your account is protected. You'll be asked for a 6-digit code at every sign-in."
                       : required ? "As a platform administrator, you are strongly encouraged to enable 2FA immediately."
                                  : "Optional but recommended for all accounts."}
            </p>
          </div>
        </div>
      </div>

      {!enabled && !enroll && (
        <div className="card-flat">
          <div className="label-mono">Enable 2FA</div>
          <p className="mt-3 text-sm text-stone-600">Click below to generate your unique authenticator secret.</p>
          <button data-testid="2fa-start" onClick={start} disabled={loading} className="btn-primary mt-4">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Set up 2FA
          </button>
        </div>
      )}

      {enroll && (
        <div className="card-flat space-y-4">
          <div className="label-mono">Step 1 — Scan the QR code</div>
          <p className="text-sm text-stone-600">Open your authenticator app and scan this QR code.</p>
          <img src={enroll.qr_data_url} alt="Scan with authenticator" className="mx-auto rounded-md border border-stone-200 bg-white p-4" style={{ width: 220, height: 220 }} />
          <div className="rounded-md bg-stone-50 border border-stone-200 p-3">
            <div className="label-mono !text-[10px] mb-1">Manual entry secret</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm text-slate-950 break-all">{enroll.secret}</code>
              <button onClick={() => { navigator.clipboard.writeText(enroll.secret); toast.success("Copied"); }} className="btn-ghost !text-xs"><Copy className="h-3 w-3" /></button>
            </div>
          </div>
          <div className="label-mono">Step 2 — Enter the 6-digit code</div>
          <div className="flex items-center gap-3">
            <input
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" inputMode="numeric"
              data-testid="2fa-verify-code"
              className="w-40 rounded-md border border-stone-300 bg-white px-3 py-2 font-mono text-lg tracking-[0.4em] text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            />
            <button onClick={verify} disabled={loading || code.length !== 6} data-testid="2fa-verify" className="btn-primary">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Enable"}
            </button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="card-flat">
          <div className="label-mono !text-red-600">Disable 2FA</div>
          <p className="mt-3 text-sm text-stone-600">This will remove your second factor. You'll need your password and a current 6-digit code.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" data-testid="2fa-disable-password" className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" data-testid="2fa-disable-code" className="rounded-md border border-stone-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
          </div>
          <button onClick={disable} disabled={loading} data-testid="2fa-disable" className="btn-secondary mt-4 !text-red-600">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable 2FA"}
          </button>
        </div>
      )}
    </div>
  );
}
